/* ---------------------------------------------------------------------------
   Get the highlights
   Wraps the theme's Swiper instance with tablist semantics, a segmented
   progress control and video-aware autoplay. No third party code.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  function Highlights(root) {
    this.root = root;
    this.init();
  }

  Highlights.prototype.init = function () {
    var self = this;
    var root = this.root;

    this.container = root.querySelector('.hp-gth__carousel');
    this.slides = [].slice.call(root.querySelectorAll('.hp-gth__slide'));
    if (!this.container || !this.slides.length) return;

    this.tabs = [].slice.call(root.querySelectorAll('.hp-gth__tab'));
    this.segs = [].slice.call(root.querySelectorAll('.hp-gth__seg'));
    this.fills = this.segs.map(function (s) { return s.querySelector('.hp-gth__seg-fill'); });
    this.playBtn = root.querySelector('.hp-gth__play');
    this.prevBtn = root.querySelector('.hp-gth__arrow--prev');
    this.nextBtn = root.querySelector('.hp-gth__arrow--next');

    this.duration = parseFloat(root.getAttribute('data-duration')) || 6;
    this.gap = parseInt(root.getAttribute('data-gap'), 10) || 16;
    this.autoplay = root.getAttribute('data-autoplay') === 'true';
    this.loop = root.getAttribute('data-loop') !== 'false';
    this.drag = root.getAttribute('data-drag') !== 'false';
    this.wheel = root.getAttribute('data-wheel') !== 'false';

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reduced) this.autoplay = false;

    this.index = 0;
    this.elapsed = 0;
    this.last = 0;
    this.hovering = false;
    this.onScreen = true;
    this.userPaused = !this.autoplay;

    var Swiper = window.MinimogLibs && window.MinimogLibs.Swiper;
    if (!Swiper) return;

    this.swiper = new Swiper(this.container, {
      slidesPerView: 'auto',
      spaceBetween: this.gap,
      grabCursor: this.drag,
      simulateTouch: this.drag,
      speed: 500,
      threshold: 5,
      watchSlidesProgress: true,
      a11y: false,
      keyboard: { enabled: true, onlyInViewport: true },
      on: {
        slideChange: function () { self.onSlideChange(this.activeIndex); }
      }
    });

    this.bindArrows();
    if (this.wheel) this.bindWheel();
    this.bindTabs();
    this.bindSegments();
    this.bindPlay();
    this.bindPause();
    this.observe();

    this.onSlideChange(0);
    this.setPlaying(!this.userPaused);
    this.start();
  };

  /* ---- arrows ---------------------------------------------------------- */
  Highlights.prototype.bindArrows = function () {
    var self = this;
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        if (self.index === 0 && self.loop) self.goTo(self.slides.length - 1);
        else self.swiper.slidePrev();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        if (self.index === self.slides.length - 1 && self.loop) self.goTo(0);
        else self.swiper.slideNext();
      });
    }
  };

  Highlights.prototype.syncArrows = function () {
    // with looping on, the arrows wrap instead of dead-ending
    if (this.prevBtn) this.prevBtn.disabled = !this.loop && this.index === 0;
    if (this.nextBtn) this.nextBtn.disabled = !this.loop && this.index === this.slides.length - 1;
  };

  /* ---- horizontal wheel / trackpad -------------------------------------
     The theme's Swiper build ships without the mousewheel module, so this
     does the same job by hand. Only horizontal gestures are captured, and
     the edges are released, so vertical page scrolling is never hijacked. */
  Highlights.prototype.bindWheel = function () {
    var self = this;
    var acc = 0;
    var locked = false;
    var endTimer = null;
    var THRESHOLD = 40;
    var GESTURE_END = 180;

    this.container.addEventListener('wheel', function (e) {
      // vertical gesture: leave it to the page
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      // release at the edges so the page can keep scrolling
      if (self.swiper.isBeginning && e.deltaX < 0) return;
      if (self.swiper.isEnd && e.deltaX > 0) return;

      e.preventDefault();

      // A trackpad flick emits a long tail of inertia events. Treat the whole
      // burst as one gesture: the lock only lifts once the events stop for
      // GESTURE_END ms, otherwise the tail advances a second slide.
      if (endTimer) window.clearTimeout(endTimer);
      endTimer = window.setTimeout(function () {
        locked = false;
        acc = 0;
      }, GESTURE_END);

      if (locked) return;

      acc += e.deltaX;
      if (Math.abs(acc) < THRESHOLD) return;

      locked = true;
      if (acc > 0) self.swiper.slideNext();
      else self.swiper.slidePrev();
      acc = 0;
    }, { passive: false });
  };

  /* ---- tablist ---------------------------------------------------------- */
  Highlights.prototype.bindTabs = function () {
    var self = this;
    this.tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        self.goTo(i);
        tab.focus();
      });
      tab.addEventListener('keydown', function (e) {
        var last = self.tabs.length - 1;
        var next = null;
        if (e.key === 'ArrowRight') next = i === last ? 0 : i + 1;
        else if (e.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = last;
        if (next === null) return;
        e.preventDefault();
        self.goTo(next);
        self.tabs[next].focus();
      });
    });
  };

  Highlights.prototype.bindSegments = function () {
    var self = this;
    this.segs.forEach(function (seg, i) {
      seg.addEventListener('click', function () { self.goTo(i); });
    });
  };

  Highlights.prototype.bindPlay = function () {
    var self = this;
    if (!this.playBtn) return;
    this.playBtn.addEventListener('click', function () {
      self.userPaused = !self.userPaused;
      self.setPlaying(!self.userPaused);
      if (!self.userPaused) self.last = 0;
      self.syncVideo();
    });
  };

  /* ---- pause on interaction --------------------------------------------- */
  Highlights.prototype.bindPause = function () {
    var self = this;
    var hold = function () { self.hovering = true; self.syncVideo(); };
    var release = function () { self.hovering = false; self.last = 0; self.syncVideo(); };
    this.root.addEventListener('mouseenter', hold);
    this.root.addEventListener('mouseleave', release);
    this.root.addEventListener('focusin', hold);
    this.root.addEventListener('focusout', function (e) {
      if (!self.root.contains(e.relatedTarget)) release();
    });
    this.container.addEventListener('touchstart', hold, { passive: true });
    this.container.addEventListener('touchend', release, { passive: true });
    document.addEventListener('visibilitychange', function () {
      self.onScreen = !document.hidden && self.inView !== false;
      self.last = 0;
      self.syncVideo();
    });
  };

  Highlights.prototype.observe = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      self.inView = entries[0].isIntersecting;
      self.onScreen = self.inView && !document.hidden;
      self.last = 0;
      self.syncVideo();
    }, { threshold: 0.25 });
    io.observe(this.root);
    this.io = io;
  };

  /* ---- state ------------------------------------------------------------ */
  Highlights.prototype.running = function () {
    return !this.userPaused && !this.hovering && this.onScreen !== false;
  };

  Highlights.prototype.setPlaying = function (on) {
    this.root.setAttribute('data-playing', on ? 'true' : 'false');
    if (this.playBtn) {
      this.playBtn.setAttribute('aria-pressed', on ? 'false' : 'true');
      this.playBtn.setAttribute('aria-label', this.playBtn.getAttribute(on ? 'data-label-pause' : 'data-label-play') || '');
    }
  };

  Highlights.prototype.currentVideo = function () {
    var slide = this.slides[this.index];
    return slide ? slide.querySelector('video') : null;
  };

  Highlights.prototype.syncVideo = function () {
    var v = this.currentVideo();
    if (!v) return;
    if (this.running()) {
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    } else {
      v.pause();
    }
  };

  Highlights.prototype.goTo = function (i) {
    if (this.swiper) this.swiper.slideTo(i);
    else this.onSlideChange(i);
  };

  Highlights.prototype.onSlideChange = function (i) {
    var self = this;
    this.index = i;
    this.elapsed = 0;
    this.last = 0;

    this.tabs.forEach(function (tab, n) {
      var on = n === i;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.setAttribute('tabindex', on ? '0' : '-1');
    });
    this.segs.forEach(function (seg, n) {
      seg.setAttribute('aria-current', n === i ? 'true' : 'false');
    });
    this.slides.forEach(function (slide, n) {
      slide.setAttribute('aria-hidden', n === i ? 'false' : 'true');
      var v = slide.querySelector('video');
      if (v && n !== i) { v.pause(); try { v.currentTime = 0; } catch (e) {} }
    });
    this.syncArrows();
    this.fills.forEach(function (fill, n) {
      if (!fill) return;
      fill.style.transform = 'scaleX(' + (n < i ? 1 : 0) + ')';
    });
    self.syncVideo();
  };

  /* ---- progress loop ---------------------------------------------------- */
  Highlights.prototype.start = function () {
    var self = this;
    var step = function (ts) {
      self.tick(ts);
      self.raf = window.requestAnimationFrame(step);
    };
    this.raf = window.requestAnimationFrame(step);
  };

  Highlights.prototype.tick = function (ts) {
    if (!this.last) this.last = ts;
    var dt = (ts - this.last) / 1000;
    this.last = ts;

    var v = this.currentVideo();
    var p;

    if (v && v.duration && isFinite(v.duration)) {
      p = v.currentTime / v.duration;
    } else {
      if (this.running()) this.elapsed += dt;
      p = this.elapsed / this.duration;
    }
    if (p > 1) p = 1;
    if (p < 0) p = 0;

    var fill = this.fills[this.index];
    if (fill) fill.style.transform = 'scaleX(' + p + ')';

    if (p >= 1 && this.running()) this.advance();
  };

  Highlights.prototype.advance = function () {
    var last = this.slides.length - 1;
    if (this.index >= last) {
      if (!this.loop) { this.userPaused = true; this.setPlaying(false); return; }
      this.goTo(0);
    } else {
      this.goTo(this.index + 1);
    }
  };

  Highlights.prototype.destroy = function () {
    if (this.raf) window.cancelAnimationFrame(this.raf);
    if (this.io) this.io.disconnect();
    if (this.swiper && this.swiper.destroy) this.swiper.destroy(true, true);
  };

  /* ---- boot ------------------------------------------------------------- */
  var registry = [];
  function boot(scope) {
    var nodes = (scope || document).querySelectorAll('.hp-gth');
    [].slice.call(nodes).forEach(function (el) {
      if (el.hpHighlights) return;
      el.hpHighlights = new Highlights(el);
      registry.push(el.hpHighlights);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('.hp-gth');
    if (el && el.hpHighlights) { el.hpHighlights.destroy(); el.hpHighlights = null; }
  });
  document.addEventListener('shopify:block:select', function (e) {
    var el = e.target.closest ? e.target.closest('.hp-gth') : null;
    if (!el || !el.hpHighlights) return;
    var slides = [].slice.call(el.querySelectorAll('.hp-gth__slide'));
    var i = slides.indexOf(e.target.classList.contains('hp-gth__slide') ? e.target : e.target.closest('.hp-gth__slide'));
    if (i > -1) el.hpHighlights.goTo(i);
  });
})();
